import { useAuth } from '@nest/common-core-frontend-react';
import { CommandButton } from '@nest/internal-frontend';
import { Button, Skeleton, useValidationResultDialog } from '@nest/uikit-react';
import { useQuery } from '@tanstack/react-query';
import { useQueryState } from 'nuqs';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';
import { useCommandOutboundDeliveryOrderCreateShipment } from './outbound/commands/outbound-delivery-order-create-shipment.command';
import { useCommandOutboundDeliveryOrderPrintDocument } from './outbound/commands/outbound-delivery-order-print-document.command';
import { useCommandOutboundDeliveryOrderPrintLabels } from './outbound/commands/outbound-delivery-order-print-labels.command';
import { useCommandOutboundDeliveryOrderStartProcess } from './outbound/commands/outbound-delivery-order-start-process.command';

export default function OutboundDeliveryOrderDetailPage() {
	const [documentTypeId] = useQueryState('documentTypeId');
	const [queueId] = useQueryState('queueId');
	const id = useNumberParam('id');
	const client = useTerminalClient();
	const navigate = useNavigate();
	const auth = useAuth();

	const { data, error } = useQuery({
		queryKey: ['outbound-delivery-order', id],
		queryFn: () =>
			client.outboundDeliveryOrders.getById.execute({
				id: id!,
				populate: ['buyer', 'assignedTo', 'lines.stockItem', 'lines.stockItem.item']
			}),
		enabled: !!id
	});

	useValidationResultDialog([error]);

	const startProcessCommand = useCommandOutboundDeliveryOrderStartProcess({
		id: data?.id,
		documentTypeId,
		queueId
	});

	const commands = [
		useCommandOutboundDeliveryOrderPrintDocument({ id }),
		useCommandOutboundDeliveryOrderPrintLabels({ id }),
		useCommandOutboundDeliveryOrderCreateShipment({ id: data?.id ?? id, shipmentCreated: data?.shipmentCreated })
	];

	const buyerName = data?.buyer?.name ?? data?.buyerSnapshot?.name ?? '';
	const assignedToName = data?.assignedTo?.name ?? '';
	const lines = data?.lines ?? [];
	const remainingSum = lines.reduce((sum, l) => sum + (l.quantityPickRemaining ?? 0), 0);
	const nowLoadedSum = lines.reduce((sum, l) => sum + (l.quantityNowLoaded ?? 0), 0);

	const bottomContent = data ? (
		<>
			{data.state === 'ToProcess' || (data.state === 'InProgress' && !data.assignedTo) ? (
				<CommandButton command={startProcessCommand} className="w-full" />
			) : (
				<>
					{data.assignedTo?.id === auth.identity?.id ? (
						<>
							{data.state === 'InProgress' && (
								<Button
									variant="default"
									className="w-full"
									onClick={() => navigate(`/outbound/delivery/orders/${data.id}/process?documentTypeId=${documentTypeId}&queueId=${queueId}`)}
								>
									Otevřít doklad
								</Button>
							)}
						</>
					) : (
						<div className="p-4 text-slate-500 text-center w-full">
							Tento doklad zpracovává uživatel
							<br />
							{data.assignedTo?.name}
						</div>
					)}
				</>
			)}
		</>
	) : null;

	return (
		<TerminalScreen
			backButtonTo={`/outbound/${documentTypeId}/queues/${queueId}`}
			showBackButton
			topBar={<div>{data?.documentNumber ?? `Výdej #${id}`}</div>}
			commands={commands}
			bottomContent={bottomContent}
		>
			<div className="flex flex-col gap-4 p-2">
				{data ? (
					<div className="space-y-1">
						<div className="text-muted-foreground">Odběratel</div>
						<div className="font-medium">{buyerName}</div>
					</div>
				) : (
					<div className="space-y-2">
						<Skeleton className="h-6 w-40" />
						<Skeleton className="h-5 w-64" />
						<Skeleton className="h-5 w-40" />
					</div>
				)}

				{data ? (
					<div className="grid grid-cols-2 gap-2">
						<div className="rounded-md border p-1 text-center bg-card">
							<div className="text-xs text-muted-foreground">Zbývá vychystat</div>
							<div className="font-bold">{remainingSum} ks</div>
						</div>
						<div className="rounded-md border p-1 text-center bg-card">
							<div className="text-xs text-muted-foreground">Naloženo</div>
							<div className="font-bold">{nowLoadedSum} ks</div>
						</div>
					</div>
				) : null}

				{data ? (
					<div className="flex flex-col divide-y rounded-md border gap-2">
						{lines.map(line => (
							<div key={line.id} className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-muted/50">
								<div className="min-w-0">
									<div className="font-medium">{line.stockItem?.item?.name ?? ''}</div>
									<div className="text-muted-foreground">{line.stockItem?.item?.code ?? ''}</div>
								</div>
								<div className="ml-3 shrink-0 text-right">
									<div className="text-xs text-muted-foreground">zbývá</div>
									<div className="rounded bg-muted px-2 py-1 font-semibold">{line.quantityPickRemaining ?? 0} ks</div>
								</div>
							</div>
						))}
					</div>
				) : null}
			</div>
		</TerminalScreen>
	);
}
